import * as pdfjsLib from 'pdfjs-dist';
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { supabase } from './supabase';

// Configure PDF.js worker - use multiple sources with fallback
// Try unpkg first (more reliable), then jsdelivr, then cdnjs
const PDFJS_VERSION = pdfjsLib.version;

// Try to set up the worker with fallback sources
const workerSources = [
  `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`,
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`,
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`,
];

// Use the first source - we'll handle failures in the extraction function
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSources[0];

// Track if worker has been initialized
let workerInitialized = false;

// Timeout helper
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, errorMsg: string): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(errorMsg)), timeoutMs)
    )
  ]);
};

// ============================================================================
// TYPES FOR EXTRACTED DATA
// ============================================================================

export interface ExtractedCompany {
  companyName: string | null;
  legalStructure: string | null;
  yearEstablished: number | null;
  dunsNumber: string | null;
  ueiNumber: string | null;
  cageCode: string | null;
  address: {
    street: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
  };
  phone: string | null;
  email: string | null;
  website: string | null;
  // Social links
  linkedinUrl: string | null;
  twitterUrl: string | null;
  facebookUrl: string | null;
  valueProposition: string | null;
  coreCompetencies: string[];
  keyTechnologies: string[];
  industriesServed: string[];
  certifications: string[];
  naicsCodes: string[];
  qualifiedSetAsides: string[];
  securityClearance: string | null;
  totalEmployees: number | null;
  annualRevenue: number | null;
  confidence: number;
}

export interface WorkHistoryEntry {
  company: string;
  title: string;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
  description: string | null;
  achievements: string[];
  location: string | null;
  employmentType: string | null;
}

export interface EducationHistoryEntry {
  institution: string;
  degree: string;
  field: string | null;
  graduationYear: number | null;
  honors: string | null;
  gpa: string | null;
}

export interface MilitaryServiceEntry {
  branch: string;
  rank: string | null;
  startDate: string | null;
  endDate: string | null;
  yearsOfService: number | null;
  specialty: string | null;  // MOS, Rating, or AFSC
  honors: string[];
  dischargeType: string | null;
  isVeteran: boolean;
  serviceConnectedDisability: boolean;
  deployments: string[];
}

export interface ExtractedPerson {
  name: string;
  title: string;
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;  // LinkedIn profile URL
  yearsExperience: number | null;
  securityClearance: string | null;
  educationLevel: string | null;
  certifications: string[];
  skillsExpertise: string[];
  keyAchievements: string[];
  // Rich resume fields for enhanced proposal generation
  professionalSummary: string | null;
  workHistory: WorkHistoryEntry[];
  educationHistory: EducationHistoryEntry[];
  publications: string[];
  languages: string[];
  awards: string[];
  // Military service - important for VOSB/SDVOSB certification
  militaryService: MilitaryServiceEntry | null;
  confidence: number;
}

export interface ExtractedPastPerformance {
  // Contract Identification
  projectName: string;
  contractNumber: string | null;
  agency: string;
  subAgency: string | null;
  sectorType: 'Government' | 'Private Sector';
  contractType: 'Prime' | 'Subcontractor' | 'JV';
  rolePercentage: number | null;
  naicsCode: string | null;
  pscCode: string | null;

  // Contract Value & Period
  contractValue: number | null;
  ceilingValue: number | null;
  startDate: string | null;
  endDate: string | null;
  optionYears: number | null;
  popType: 'Virtual' | 'On-site' | 'Hybrid' | null;
  popLocation: string | null;

  // Contract Vehicle
  contractVehicle: string | null;
  vehicleNumber: string | null;
  taskOrderNumber: string | null;

  // Scope of Work
  scopeDescription: string | null;
  projectObjectives: string[];
  deliverables: string;
  serviceAreas: string[];

  // Technical Approach
  technicalApproach: string | null;
  technologiesUsed: string[];
  complianceStandards: string[];
  securityRequirements: string | null;
  qualityStandards: string[];

  // Accomplishments
  keyAccomplishments: string[];
  innovations: string[];

  // Performance Metrics
  performanceRating: number | null;
  onTimeDelivery: boolean | null;
  withinBudget: boolean | null;

  // CPARS Ratings
  cparsRecordId: string | null;
  cparsQualityRating: number | null;
  cparsScheduleRating: number | null;
  cparsCostRating: number | null;
  cparsManagementRating: number | null;
  cparsSmallBusinessRating: number | null;
  cparsOverallRating: number | null;

  // Team
  teamSize: number | null;
  fteCount: number | null;

  // Client Reference
  clientReference: {
    name: string | null;
    title: string | null;
    email: string | null;
    phone: string | null;
    organization: string | null;
  };

  // Relevance
  demonstratedCapabilities: string[];
  targetAgencies: string[];

  // Lessons Learned
  whatWorkedWell: string[];
  areasForImprovement: string[];
  lessonsLearned: string[];

  confidence: number;
}

export interface ExtractedPricing {
  labor: Array<{
    roleTitle: string;
    hourlyRate: number;
    laborCategory: string | null;
  }>;
  overhead: {
    rate: number | null;
    type: string | null;
  };
  confidence: number;
}

export interface ExtractedCapability {
  name: string;
  description: string;
  proficiencyLevel: string;
  yearsExperience: number | null;
  toolsUsed: string[];
  differentiators: string[];
  confidence: number;
}

export interface ExtractionResult {
  success: boolean;
  company: ExtractedCompany | null;
  personnel: ExtractedPerson[];
  pastPerformance: ExtractedPastPerformance[];
  pricing: ExtractedPricing | null;
  capabilities: ExtractedCapability[];
  documentSummary: string;
  extractionNotes: string[];
  text: string;
  metadata: {
    length: number;
    timestamp: string;
    documentName?: string;
    documentType?: string;
  };
}

export class DocumentAnalyzer {
  static async processFile(file: File): Promise<string> {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const fileType = file.type || '';

    console.log(`📁 [DocumentAnalyzer] Processing: ${file.name}`);
    console.log(`   Type: ${fileType || 'unknown'}, Extension: ${ext}, Size: ${(file.size / 1024).toFixed(1)}KB`);

    try {
      // PDF - check by extension OR mime type (mime type can be unreliable)
      if (ext === 'pdf' || fileType === 'application/pdf') {
        return await this.extractPdfText(file);
      }

      // Word documents
      if (
        ext === 'doc' ||
        ext === 'docx' ||
        fileType === 'application/msword' ||
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        return await this.extractDocText(file);
      }

      // Excel documents
      if (
        ext === 'xls' ||
        ext === 'xlsx' ||
        ext === 'csv' ||
        fileType === 'application/vnd.ms-excel' ||
        fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileType === 'text/csv'
      ) {
        console.log(`📊 Processing Excel file: ${file.name} (type: ${file.type}, ext: ${ext})`);
        return await this.extractExcelText(file);
      }

      // Plain text
      if (ext === 'txt' || fileType === 'text/plain') {
        console.log(`📄 [TXT] Reading plain text file...`);
        const text = await file.text();
        console.log(`✅ [TXT] Extraction complete: ${text.length} characters`);
        return text;
      }

      // If we can't determine the type, try to read as text
      console.warn(`⚠️ Unknown file type: ${fileType || 'none'} (ext: ${ext}), attempting text read...`);
      try {
        const text = await file.text();
        if (text && text.length > 0) {
          return text;
        }
      } catch (textError) {
        // Ignore text read failure
      }

      throw new Error(`Unsupported file type: ${fileType || ext || 'unknown'}`);
    } catch (error: any) {
      console.error(`❌ [DocumentAnalyzer] Failed to process ${file.name}:`, error);
      throw new Error(error.message || 'Failed to process document');
    }
  }

  private static async extractPdfText(file: File): Promise<string> {
    console.log(`📄 [PDF] Starting extraction for: ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);

    // Read file first (this should be fast)
    let arrayBuffer: ArrayBuffer;
    try {
      arrayBuffer = await withTimeout(
        file.arrayBuffer(),
        30000,
        'Timeout reading PDF file'
      );
      console.log(`📄 [PDF] File read complete (${(arrayBuffer.byteLength / 1024).toFixed(1)}KB), loading document...`);
    } catch (readError: any) {
      console.error('❌ [PDF] Failed to read file:', readError);
      throw new Error(`Failed to read PDF file: ${readError.message}`);
    }

    // Try extraction with different worker sources if needed
    let lastError: Error | null = null;

    for (let workerIndex = 0; workerIndex < workerSources.length; workerIndex++) {
      try {
        // Set worker source for this attempt
        const workerSrc = workerSources[workerIndex];
        if (workerIndex > 0) {
          console.log(`📄 [PDF] Trying fallback worker source ${workerIndex + 1}/${workerSources.length}...`);
          pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
        }

        // Configure PDF loading with proper settings
        const loadingTask = pdfjsLib.getDocument({
          data: arrayBuffer,
          cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/cmaps/`,
          cMapPacked: true,
          // Disable features that can cause hangs
          disableFontFace: true,
          useSystemFonts: true,
          // Use fake worker if real worker fails to load
          isEvalSupported: false,
        });

        // Load PDF with shorter timeout for worker loading issues
        const pdf = await withTimeout(
          loadingTask.promise,
          30000, // 30 seconds - if worker isn't loading, try next source
          'Timeout loading PDF document'
        );

        console.log(`📄 [PDF] Document loaded: ${pdf.numPages} pages`);
        workerInitialized = true;

        let text = '';
        const maxPages = Math.min(pdf.numPages, 200); // Limit to 200 pages max

        for (let i = 1; i <= maxPages; i++) {
          try {
            // Add timeout per page (10 seconds)
            const page = await withTimeout(
              pdf.getPage(i),
              10000,
              `Timeout loading page ${i}`
            );

            const content = await withTimeout(
              page.getTextContent(),
              10000,
              `Timeout extracting text from page ${i}`
            );

            const pageText = content.items
              .map((item: any) => item.str || '')
              .join(' ')
              .trim();

            if (pageText) {
              text += pageText + '\n';
            }

            // Log progress every 10 pages
            if (i % 10 === 0) {
              console.log(`📄 [PDF] Processed ${i}/${maxPages} pages...`);
            }
          } catch (pageError: any) {
            console.warn(`⚠️ [PDF] Error on page ${i}, skipping:`, pageError.message);
            // Continue with other pages even if one fails
          }
        }

        // Clean up
        try {
          pdf.destroy();
        } catch (destroyError) {
          // Ignore cleanup errors
        }

        if (!text.trim()) {
          console.warn(`⚠️ [PDF] No text extracted from ${file.name} - might be scanned/image PDF`);
          throw new Error('No text content found in PDF - the document may be scanned or image-based');
        }

        console.log(`✅ [PDF] Extraction complete: ${text.length} characters from ${maxPages} pages`);
        return text;

      } catch (error: any) {
        console.error(`❌ [PDF] Extraction attempt ${workerIndex + 1} failed:`, error.message);
        lastError = error;

        // If this was a timeout on loading (likely worker issue), try next source
        if (error.message?.includes('Timeout loading PDF') && workerIndex < workerSources.length - 1) {
          console.log(`📄 [PDF] Will try fallback worker source...`);
          continue;
        }

        // For other errors, don't retry with different worker
        break;
      }
    }

    // All attempts failed
    const error = lastError || new Error('PDF extraction failed');

    // Provide helpful error messages
    if (error.message?.includes('Timeout')) {
      throw new Error('PDF extraction timed out - the PDF.js worker may not be loading. Try refreshing the page or contact support.');
    }
    if (error.message?.includes('Invalid PDF')) {
      throw new Error('Invalid or corrupted PDF file');
    }
    if (error.message?.includes('Password')) {
      throw new Error('PDF is password protected');
    }
    if (error.message?.includes('No text content')) {
      throw error;
    }

    throw new Error(`Failed to extract text from PDF: ${error.message || 'Unknown error'}`);
  }

  private static async extractDocText(file: File): Promise<string> {
    console.log(`📝 [DOC] Starting extraction for: ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);

    try {
      // Read file with timeout
      const arrayBuffer = await withTimeout(
        file.arrayBuffer(),
        30000,
        'Timeout reading Word document'
      );

      console.log(`📝 [DOC] File read complete, extracting text...`);

      // Extract with mammoth (with timeout)
      const result = await withTimeout(
        mammoth.extractRawText({ arrayBuffer }),
        60000,
        'Timeout extracting text from Word document'
      );

      const text = result.value || '';

      if (!text.trim()) {
        console.warn(`⚠️ [DOC] No text extracted from ${file.name}`);
        throw new Error('No text content found in Word document');
      }

      console.log(`✅ [DOC] Extraction complete: ${text.length} characters`);
      return text;

    } catch (error: any) {
      console.error('❌ [DOC] Extraction failed:', error);

      if (error.message?.includes('Timeout')) {
        throw new Error('Word document extraction timed out - try a smaller file');
      }

      throw new Error(`Failed to extract text from Word document: ${error.message || 'Unknown error'}`);
    }
  }

  private static async extractExcelText(file: File): Promise<string> {
    console.log(`📊 [Excel] Starting extraction for: ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);

    try {
      // Read file with timeout
      const arrayBuffer = await withTimeout(
        file.arrayBuffer(),
        30000,
        'Timeout reading Excel file'
      );

      console.log(`📊 [Excel] File read complete, parsing workbook...`);

      // Parse workbook with timeout
      const workbook = await withTimeout(
        Promise.resolve(XLSX.read(arrayBuffer, { type: 'array' })),
        30000,
        'Timeout parsing Excel workbook'
      );

      console.log(`📊 [Excel] Workbook loaded: ${workbook.SheetNames.length} sheets`);

      let text = '';
      const maxSheets = Math.min(workbook.SheetNames.length, 20); // Limit to 20 sheets

      for (let i = 0; i < maxSheets; i++) {
        const sheetName = workbook.SheetNames[i];
        try {
          const worksheet = workbook.Sheets[sheetName];
          if (worksheet) {
            // Use sheet_to_csv for more reliable text extraction
            const sheetText = XLSX.utils.sheet_to_csv(worksheet, { blankrows: false });
            if (sheetText.trim()) {
              text += `=== Sheet: ${sheetName} ===\n${sheetText}\n\n`;
            }
          }
        } catch (sheetError) {
          console.warn(`⚠️ [Excel] Error processing sheet "${sheetName}":`, sheetError);
        }
      }

      if (!text.trim()) {
        console.warn(`⚠️ [Excel] No text extracted from ${file.name}`);
        throw new Error('No text content found in Excel file');
      }

      console.log(`✅ [Excel] Extraction complete: ${text.length} characters from ${maxSheets} sheets`);
      return text;

    } catch (error: any) {
      console.error('❌ [Excel] Extraction failed:', error);

      if (error.message?.includes('Timeout')) {
        throw new Error('Excel extraction timed out - try a smaller file');
      }

      throw new Error(`Failed to extract text from Excel file: ${error.message || 'Unknown error'}`);
    }
  }

  private static async summarizeText(text: string, maxLength: number = 10000): Promise<string> {
    // If text is shorter than max length, return as is
    if (text.length <= maxLength) {
      return text;
    }

    // Simple truncation with context preservation
    const truncated = text.substring(0, maxLength);
    const lastPeriod = truncated.lastIndexOf('.');
    
    // If we found a period, cut at the last complete sentence
    if (lastPeriod > 0) {
      return truncated.substring(0, lastPeriod + 1) + '\n[Content truncated...]';
    }
    
    // If no period found, cut at the last space to avoid word breaks
    const lastSpace = truncated.lastIndexOf(' ');
    return truncated.substring(0, lastSpace) + '...\n[Content truncated...]';
  }

  /**
   * Legacy analyze method - returns basic text analysis
   * Use extractStructuredData for AI-powered extraction
   */
  static async analyzeDocument(text: string): Promise<any> {
    return {
      text: await this.summarizeText(text),
      metadata: {
        length: text.length,
        timestamp: new Date().toISOString()
      }
    };
  }

  /**
   * AI-powered structured data extraction using GPT-4o
   * Extracts company info, personnel, past performance, pricing, and capabilities
   */
  static async extractStructuredData(
    text: string,
    options?: {
      documentName?: string;
      documentType?: string;
      extractionMode?: 'full' | 'company' | 'personnel' | 'past_performance' | 'pricing' | 'capabilities';
    }
  ): Promise<ExtractionResult> {
    console.log('📄 Starting AI-powered extraction...');
    console.log(`   Document length: ${text.length} characters`);

    try {
      const { data, error } = await supabase.functions.invoke('extract-profile-data', {
        body: {
          documentText: text,
          documentType: options?.documentType,
          documentName: options?.documentName,
          extractionMode: options?.extractionMode || 'full'
        }
      });

      if (error) {
        console.error('❌ Extraction edge function error:', error);
        throw new Error(`Extraction failed: ${error.message}`);
      }

      if (!data.success) {
        console.error('❌ Extraction returned unsuccessful:', data.extractionNotes);
        return {
          success: false,
          company: null,
          personnel: [],
          pastPerformance: [],
          pricing: null,
          capabilities: [],
          documentSummary: data.documentSummary || 'Extraction failed',
          extractionNotes: data.extractionNotes || ['Extraction was not successful'],
          text: await this.summarizeText(text),
          metadata: {
            length: text.length,
            timestamp: new Date().toISOString(),
            documentName: options?.documentName,
            documentType: options?.documentType
          }
        };
      }

      console.log('✅ Extraction successful!');
      console.log(`   Company: ${data.company ? 'found' : 'not found'}`);
      console.log(`   Personnel: ${data.personnel?.length || 0} records`);
      console.log(`   Past Performance: ${data.pastPerformance?.length || 0} records`);
      console.log(`   Capabilities: ${data.capabilities?.length || 0} records`);

      return {
        success: true,
        company: data.company,
        personnel: data.personnel || [],
        pastPerformance: data.pastPerformance || [],
        pricing: data.pricing,
        capabilities: data.capabilities || [],
        documentSummary: data.documentSummary || 'Document analyzed successfully',
        extractionNotes: data.extractionNotes || [],
        text: await this.summarizeText(text),
        metadata: {
          length: text.length,
          timestamp: new Date().toISOString(),
          documentName: options?.documentName,
          documentType: options?.documentType
        }
      };
    } catch (err: any) {
      console.error('❌ Extraction exception:', err);

      // Return a graceful failure with the raw text
      return {
        success: false,
        company: null,
        personnel: [],
        pastPerformance: [],
        pricing: null,
        capabilities: [],
        documentSummary: 'AI extraction failed',
        extractionNotes: [`Error: ${err.message}`],
        text: await this.summarizeText(text),
        metadata: {
          length: text.length,
          timestamp: new Date().toISOString(),
          documentName: options?.documentName,
          documentType: options?.documentType
        }
      };
    }
  }

  /**
   * Process a file and extract structured data in one call
   * Combines processFile + extractStructuredData
   */
  static async processAndExtract(
    file: File,
    options?: {
      documentType?: string;
      extractionMode?: 'full' | 'company' | 'personnel' | 'past_performance' | 'pricing' | 'capabilities';
    }
  ): Promise<ExtractionResult> {
    console.log(`📁 Processing file: ${file.name} (${file.type})`);

    // Step 1: Extract raw text
    const text = await this.processFile(file);
    console.log(`📝 Extracted ${text.length} characters of text`);

    // Step 2: Run AI extraction
    return this.extractStructuredData(text, {
      documentName: file.name,
      documentType: options?.documentType || this.inferDocumentType(file.name),
      extractionMode: options?.extractionMode || 'full'
    });
  }

  /**
   * Infer document type from filename
   */
  private static inferDocumentType(filename: string): string {
    const lower = filename.toLowerCase();

    if (lower.includes('capability') || lower.includes('cap-statement')) {
      return 'Capability Statement';
    }
    if (lower.includes('resume') || lower.includes('cv')) {
      return 'Resume/CV';
    }
    if (lower.includes('ppq') || lower.includes('past-performance') || lower.includes('pastperf')) {
      return 'Past Performance';
    }
    if (lower.includes('rate') || lower.includes('pricing') || lower.includes('labor')) {
      return 'Rate Card/Pricing';
    }
    if (lower.includes('cpars')) {
      return 'CPARS Report';
    }
    if (lower.includes('contract') || lower.includes('award')) {
      return 'Contract Document';
    }

    return 'Unknown';
  }
}

export default DocumentAnalyzer;